import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Crown, Lock, Calendar, MapPin, Users, Bell, Sparkles } from 'lucide-react';

const EVENTS = [
  {
    title: "OPENING",
    date: "Septembre 2026",
    location: "Studio PURO",
    description: "Inauguration de votre studio. Cocktail, matcha et café glacé en collaboration avec Nomad Coffee & Co, découverte du studio et rencontre avec l'équipe PURO.",
    tag: "Événement",
    spots: "Sur invitation"
  },
  {
    title: "Pilates au milieu des vignes",
    date: "Octobre 2026",
    location: "Domaine viticole partenaire",
    description: "Une séance en plein air dans un cadre exceptionnel, suivie d'une dégustation. Une expérience unique réservée à nos membres.",
    tag: "Plein air",
    spots: "12 places"
  },
  {
    title: "Atelier Sophrologie & Lâcher-prise",
    date: "Novembre 2026",
    location: "Studio PURO",
    description: "Un moment de reconnexion profonde avec vous-même. Techniques de respiration, relaxation guidée et méditation.",
    tag: "Atelier",
    spots: "6 places"
  },
  {
    title: "Masterclass Posture & Mobilité",
    date: "Décembre 2026",
    location: "Studio PURO",
    description: "Une séance approfondie pour comprendre votre corps, corriger votre posture et améliorer votre mobilité au quotidien.",
    tag: "Masterclass",
    spots: "6 places"
  },
  {
    title: "Brunch & Pilates — Édition spéciale",
    date: "Janvier 2027",
    location: "Lieu partenaire",
    description: "Séance matinale suivie d'un brunch healthy dans un lieu d'exception. Le bien-être sous toutes ses formes.",
    tag: "Événement",
    spots: "Sur invitation"
  }
];

const TAG_STYLES = {
  "Événement": "bg-[#C99A55]/15 text-[#C99A55]",
  "Plein air": "bg-[#8C9B86]/15 text-[#6B7A65]",
  "Atelier": "bg-[#C76542]/15 text-[#A67463]",
  "Masterclass": "bg-[#493329]/10 text-[#493329]",
};

export default function ClubPrivilege() {
  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Header */}
      <div className="gradient-cream py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-[#C99A55]/15 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-6 h-6 text-[#C99A55]" strokeWidth={1.5} />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99A55] mb-2 font-semibold">Exclusif membres</p>
          <h1 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight">
            Club Privilège
          </h1>
          <div className="divider-premium" />
          <p className="text-sm text-[#7A6455] max-w-md mx-auto">
            Des événements privés et des expériences uniques réservés à nos membres. Bien plus que du Pilates.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Locked banner */}
        <Card className="bg-[#493329] text-white rounded-2xl mb-8" data-testid="club-locked-banner">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C99A55]/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-heading font-medium text-lg">Programme à venir</p>
                <p className="text-white/50 text-sm">Les inscriptions aux événements seront ouvertes dès le lancement du studio. Voici un aperçu de ce qui vous attend.</p>
              </div>
              <Link to="/contact" className="flex-shrink-0 hidden sm:block">
                <Button size="sm" className="bg-[#C99A55] hover:bg-[#C99A55]/80 text-white rounded-full text-xs btn-premium">
                  <Bell className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Être informée
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Events */}
        <div className="space-y-4">
          {EVENTS.map((event, idx) => (
            <Card key={idx} className="bg-white border border-[#E9DDCC] rounded-2xl overflow-hidden opacity-80 card-premium animate-fade-up" style={{ animationDelay: `${idx * 0.08}s` }}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium ${TAG_STYLES[event.tag] || 'bg-[#F0E8D9] text-[#7A6455]'}`}>
                        {event.tag}
                      </span>
                      <span className="text-xs text-[#AE9885]">{event.spots}</span>
                    </div>
                    <h3 className="font-heading font-medium text-lg text-[#493329] mb-2">{event.title}</h3>
                    <p className="text-sm text-[#7A6455] leading-relaxed mb-3">{event.description}</p>
                    <div className="flex items-center gap-4 text-xs text-[#AE9885]">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                        {event.date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                        {event.location}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Button disabled className="rounded-full px-4 h-9 text-xs bg-[#E9DDCC] text-[#AE9885] cursor-not-allowed">
                      <Lock className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Bientôt
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom message */}
        <div className="mt-12 text-center">
          <p className="text-xs text-[#AE9885] italic">Le programme définitif sera communiqué à l'ouverture du studio — septembre 2026.</p>
          <p className="text-xs text-[#AE9885] italic mt-1">Ouverture anticipée probable.</p>
        </div>
      </div>
    </div>
  );
}
